import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'
import Text, { H2 } from 'components/common/Text/Text'
import Button from 'components/common/Button/Button'
import Mixin from 'components/common/Mixin'
import Icon from 'components/common/Icon/Icon'
import Popover from 'components/common/Popover'
import { ALERT_TEXT } from './constants'
import OrSeparator from 'components/common/OrSeparator/OrSeparator'

const AppointmentsLocationItem = ({
  storeId,
  selected,
  savedStore,
  clickHandler,
  address,
  street,
  distanceRadius,
  appointmentEnable,
  phoneNumbers = [],
  selectStoreHandler,
  isSaved,
  handlePhoneLinkClick
}) =>
  <Wrapper
    data-at-storelocator-store
    data-at-storelocator-storeid={ storeId }
    data-at-storelocator-store-appointment
    selected={ selected }
    tabIndex="0"
    onKeyDown={ clickHandler }
    onClick={ clickHandler }>
    {
      isSaved &&
        <Checked alt="" asset="check-circle" />
    }
    <TextWrapper data-at-storelocator-store-fulladdress mobileFlex>
      <H2 data-at-store-address noMargin>
        <Text data-at-storelocator-store-street bolder block size={ 18 }>{ street }</Text>
        <Text data-at-storelocator-store-address bolder block size={ 18 }>{ address }</Text>
      </H2>
      <Media.Mobile>
        <Text data-at-storelocator-store-distance><Icon alt="" asset="location-red" margin="-2px 4px 0 0" /> { distanceRadius } Miles</Text>
      </Media.Mobile>
    </TextWrapper>
    <Media.Mobile>
      <TextWrapper>
        {
          appointmentEnable &&
          <StyledPopover
            asset="calendar-red"
            alternative="calendar-red"
            ariaLabel="Online appointments enabled"
            innerProps={{
              popoverIconProps: {
                'data-at-storelocator-store-calendar-icon': true
              }
            }}>
            This store accepts online appointment requests.
          </StyledPopover>
        }
      </TextWrapper>
    </Media.Mobile>
    <Media.Desktop>
      <TextWrapper>
        <Text data-at-storelocator-store-distance><Icon alt="" asset="location-red" margin="-2px 4px 0 0" /> { distanceRadius } Miles</Text>
        {
          appointmentEnable &&
          <StyledPopover
            asset="calendar-red"
            alternative="calendar-red"
            ariaLabel="Online appointments enabled"
            innerProps={{
              popoverIconProps: {
                'data-at-storelocator-store-calendar-icon': true
              }
            }}>
            This store accepts online appointment requests.
          </StyledPopover>
        }
      </TextWrapper>
    </Media.Desktop>
    <ButtonWrapper>
      {
        appointmentEnable ?
          <SelectStoreButton
            data-at-storelocator-store-selectbutton
            disabled={ isSaved }
            small
            narrow
            full="mobile"
            onClick={ selectStoreHandler }>
            select store
          </SelectStoreButton> :
          <Alert>{ ALERT_TEXT }</Alert>
      }
    </ButtonWrapper>
    <OrSeparator />
    <CallStoreText>Call store to get help or schedule appointment.</CallStoreText>
    {
      phoneNumbers
        .map((phone, index) =>
          <React.Fragment key={ index }>
            <Media.Desktop>
              <PhoneText>
                { phone }
              </PhoneText>
            </Media.Desktop>
            <Media.Mobile>
              <PhoneButtonLink href={ `tel:+1${phone}` }>
                <Icon asset="phone-red" height={ 13 } width={ 13 } />
                <PhoneRed>{ phone }</PhoneRed>
              </PhoneButtonLink>
            </Media.Mobile>
          </React.Fragment>
        )
    }
  </Wrapper>

export default AppointmentsLocationItem

AppointmentsLocationItem.propTypes = {
  address: PropTypes.string,
  appointmentEnable: PropTypes.bool,
  clickHandler: PropTypes.func,
  distanceRadius: PropTypes.number,
  handlePhoneLinkClick: PropTypes.func,
  isSaved: PropTypes.bool,
  phoneNumbers: PropTypes.arrayOf(PropTypes.string),
  savedStore: PropTypes.bool,
  selected: PropTypes.bool,
  selectStoreHandler: PropTypes.func,
  storeId: PropTypes.string,
  street: PropTypes.string
}

const Wrapper = styled.li`
  position: relative;
  cursor: pointer;
  ${Media.desktop`
    padding: 20px 20px 20px 20px;
    border-bottom: 1px solid ${Theme.colors.light_grey};
  `}
  ${Media.mobile`
    margin-bottom: 12px;
    padding: 20px 20px 20px 20px;
    border: 1px solid ${Theme.colors.light_grey};
  `}
  ${props => props.selected && css`
    && {
      border: 2px solid ${Theme.colors.black};
    }
  `}
  ${Mixin.clearFix()}
`

const Checked = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
`

const TextWrapper = styled.div`
  padding-right: 20%;
  margin-bottom: 20px;
  ${Media.mobile`
    ${props => props.mobileFlex && css`
      display: flex;
      justify-content: space-between;
      padding-right: 0;
    `}
  `}
`

const ButtonWrapper = styled.div`
  & ${Button} {
    padding: 7px 16px 8px;
  }
`

const SelectStoreButton = styled(Button)`
  width: 93%;
  ${Media.mobile`
    width: 100%;
  `}
`

const Alert = styled.div`
  width: 100%;
  height: 40px;
  background-color: ${Theme.colors.pale_yellow};
  border: 1px solid ${Theme.colors.light_grey};
  ${Mixin.font.GothamLight(11, 300)}
`

const StyledPopover = styled(Popover)`
  margin: -2px 0 0 20px;

  && img {
    height: 20px;
  }
`

const CallStoreText = styled.p`
  margin: 0 0 15px;
  text-align: center;
  font-size: 12px;
`

const PhoneText = styled.p`
  width: 100%;
  margin: 0;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
`

const PhoneRed = styled.p`
  margin: 0 0 0 5px;
  color: ${Theme.colors.red};
`

const PhoneButtonLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  margin: 0 0 15px;
  text-decoration: none;
  text-align: center;
  border: 2px solid ${Theme.colors.red};
`
